import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { CreateCaseForm } from '@podus/ui';
import type { CaseFormData, FeeBreakdown } from '@podus/ui';
import { api } from '@/lib/api';
import type { Case, CaseCategory, FeeConfig } from '@/types';

const CASE_TYPE_TO_CATEGORY: Record<string, CaseCategory> = {
    'walk-in': 'Walk-in',
    'court-referral': 'Court Referral',
    'online': 'Online',
};

export default function CreateNewCase() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const caseType = searchParams.get('type') || 'walk-in';
    const [feeConfigs, setFeeConfigs] = useState<FeeConfig[]>([]);

    useEffect(() => {
        api.get<FeeConfig[]>('/api/settings/fees')
            .then((res) => setFeeConfigs(res.response.data ?? []))
            .catch(() => setFeeConfigs([]));
    }, []);

    const fees = useMemo<FeeBreakdown | undefined>(() => {
        const category = CASE_TYPE_TO_CATEGORY[caseType];
        const cfg = feeConfigs.find((f) => f.caseCategory === category);
        if (!cfg) return undefined;
        return { items: cfg.items, totalFee: cfg.totalFee };
    }, [feeConfigs, caseType]);

    const handleSubmit = async (type: string, formData: CaseFormData) => {
        let createdCase: Case | null = null;

        if (type === 'court-referral') {
            const res = await api.post<Case>('/api/cases/court-referral', {
                filingDate: formData.filingDate
                    ? new Date(formData.filingDate).toISOString()
                    : new Date().toISOString(),
                caseType: formData.caseType || 'Civil',
                suitNumber: formData.suitNumber || '',
                lawyerName: formData.lawyerName,
                lawFirm: formData.lawFirm,
                lawyerPhone: formData.lawyerPhone,
                lawyerEmail: formData.lawyerEmail,
                disputeCategory: formData.disputeCategory,
                description: formData.description,
                paymentMethod: formData.paymentMethod || 'Cash',
                parties: [
                    { ...formData.defendant, role: 'Defendant', partyType: 'Individual' },
                    { ...formData.claimant, role: 'Claimant', partyType: 'Individual' },
                ].filter((p) => p.name),
            });
            createdCase = res.response.data ?? null;
        } else {
            const res = await api.post<Case>('/api/cases/walk-in', {
                disputeCategory: formData.disputeCategory,
                description: formData.description,
                paymentMethod: formData.paymentMethod || 'Cash',
                parties: [
                    ...(formData.applicants || []).map((p) => ({
                        ...p,
                        role: 'Applicant',
                        partyType: formData.applicantType,
                    })),
                    ...(formData.respondents || []).map((p) => ({
                        ...p,
                        role: 'Respondent',
                        partyType: formData.respondentType,
                    })),
                ],
            });
            createdCase = res.response.data ?? null;
        }

        if (createdCase?.id && formData.files.length > 0) {
            const uploadable = formData.files.filter((f) => f.file);
            if (uploadable.length > 0) {
                const fd = new FormData();
                uploadable.forEach((f) => fd.append('files', f.file!));
                const docNames = Array.from(formData.uploadedDocs || []);
                docNames.forEach((name) => fd.append('mandatoryDocTypes', name));
                // Pad with empty strings for any extra files so indexes align
                for (let i = docNames.length; i < uploadable.length; i++) {
                    fd.append('mandatoryDocTypes', '');
                }
                try {
                    await api.upload(`/api/cases/${createdCase.id}/evidence`, fd);
                } catch (err) {
                    console.error('Evidence upload failed:', err);
                }
            }
        }
    };

    const handleSuccess = () => {
        const isCaseManager = window.location.pathname.startsWith('/case-manager');
        navigate(isCaseManager ? '/case-manager/cases' : '/dashboard/cases');
    };

    const subtitle =
        caseType === 'court-referral'
            ? 'Create a new case from a court referral'
            : 'Create a new Multidoor Court case from a walk-in';

    return (
        <div className="bg-[#fafafa] py-6">
            <div className="bg-white rounded-lg p-6 pb-0 max-w-4xl mx-auto border border-[#E0E0E0] relative">
                <CreateCaseForm
                    caseType={caseType}
                    title="Create New Case"
                    subtitle={subtitle}
                    fees={fees}
                    onSubmit={handleSubmit}
                    onSuccess={handleSuccess}
                />
            </div>
        </div>
    );
}
